/**
 * DOM Utilities
 * Lightweight helpers for common DOM operations (jQuery replacement)
 */

/**
 * Select single element
 * @param {string} selector - CSS selector
 * @param {Element} context - Optional context element
 * @returns {Element|null}
 */
export const $ = (selector, context = document) => {
  return context.querySelector(selector);
};

/**
 * Select multiple elements
 * @param {string} selector - CSS selector
 * @param {Element} context - Optional context element
 * @returns {NodeList}
 */
export const $$ = (selector, context = document) => {
  return context.querySelectorAll(selector);
};

/**
 * Add event listener to element(s)
 * @param {string|Element|NodeList} target - Selector or element(s)
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 */
export const on = (target, event, handler) => {
  const elements = typeof target === 'string' ? $$(target) :
                   target instanceof NodeList ? target : [target];

  elements.forEach(el => {
    if (el && el.addEventListener) {
      el.addEventListener(event, handler);
    }
  });
};

/**
 * Remove event listener from element(s)
 * @param {string|Element|NodeList} target - Selector or element(s)
 * @param {string} event - Event name
 * @param {Function} handler - Event handler
 */
export const off = (target, event, handler) => {
  const elements = typeof target === 'string' ? $$(target) :
                   target instanceof NodeList ? target : [target];

  elements.forEach(el => {
    if (el && el.removeEventListener) {
      el.removeEventListener(event, handler);
    }
  });
};

/**
 * Check if element has class
 * @param {Element} el - Element
 * @param {string} className - Class name
 * @returns {boolean}
 */
export const hasClass = (el, className) => {
  return el && el.classList.contains(className);
};

/**
 * Add class to element(s)
 * @param {string|Element|NodeList} target - Selector or element(s)
 * @param {string} className - Class name
 */
export const addClass = (target, className) => {
  const elements = typeof target === 'string' ? $$(target) :
                   target instanceof NodeList ? target : [target];

  elements.forEach(el => {
    if (el && el.classList) {
      el.classList.add(className);
    }
  });
};

/**
 * Remove class from element(s)
 * @param {string|Element|NodeList} target - Selector or element(s)
 * @param {string} className - Class name
 */
export const removeClass = (target, className) => {
  const elements = typeof target === 'string' ? $$(target) :
                   target instanceof NodeList ? target : [target];

  elements.forEach(el => {
    if (el && el.classList) {
      el.classList.remove(className);
    }
  });
};

/**
 * Toggle class on element(s)
 * @param {string|Element|NodeList} target - Selector or element(s)
 * @param {string} className - Class name
 */
export const toggleClass = (target, className) => {
  const elements = typeof target === 'string' ? $$(target) :
                   target instanceof NodeList ? target : [target];

  elements.forEach(el => {
    if (el && el.classList) {
      el.classList.toggle(className);
    }
  });
};

/**
 * Show element(s)
 * @param {string|Element|NodeList} target - Selector or element(s)
 */
export const show = (target) => {
  const elements = typeof target === 'string' ? $$(target) :
                   target instanceof NodeList ? target : [target];

  elements.forEach(el => {
    if (el) {
      el.style.display = '';
    }
  });
};

/**
 * Hide element(s)
 * @param {string|Element|NodeList} target - Selector or element(s)
 */
export const hide = (target) => {
  const elements = typeof target === 'string' ? $$(target) :
                   target instanceof NodeList ? target : [target];

  elements.forEach(el => {
    if (el) {
      el.style.display = 'none';
    }
  });
};

/**
 * Get/Set attribute
 * @param {Element} el - Element
 * @param {string} attr - Attribute name
 * @param {string} value - Optional value to set
 * @returns {string|undefined}
 */
export const attr = (el, attr, value) => {
  if (!el) return;

  if (value !== undefined) {
    el.setAttribute(attr, value);
  } else {
    return el.getAttribute(attr);
  }
};

/**
 * Find closest ancestor matching selector
 * @param {Element} el - Element
 * @param {string} selector - CSS selector
 * @returns {Element|null}
 */
export const closest = (el, selector) => {
  return el ? el.closest(selector) : null;
};

/**
 * Find element within context
 * @param {Element} context - Context element
 * @param {string} selector - CSS selector
 * @returns {Element|null}
 */
export const find = (context, selector) => {
  return context ? context.querySelector(selector) : null;
};

/**
 * Find all elements within context
 * @param {Element} context - Context element
 * @param {string} selector - CSS selector
 * @returns {NodeList}
 */
export const findAll = (context, selector) => {
  return context ? context.querySelectorAll(selector) : [];
};

/**
 * Check if element exists and has length
 * @param {string|Element|NodeList} target - Selector or element(s)
 * @returns {boolean}
 */
export const exists = (target) => {
  if (typeof target === 'string') {
    return $$(target).length > 0;
  }
  if (target instanceof NodeList) {
    return target.length > 0;
  }
  return !!target;
};

/**
 * ForEach for NodeList
 * @param {NodeList|Array} list - NodeList or Array
 * @param {Function} callback - Callback function
 */
export const each = (list, callback) => {
  if (!list) return;
  Array.from(list).forEach(callback);
};

/**
 * Ready state listener (DOMContentLoaded replacement)
 * @param {Function} callback - Function to execute when DOM is ready
 */
export const ready = (callback) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
};
